import { makeStyles } from '@material-ui/core';
import { CheckCircleOutlineSharp } from '@material-ui/icons';
import clsx from 'clsx';
import React from 'react';
import { Check, Circle, Clock } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../../theme/theme';
import { Badge, HighlightCard } from '../../../global';

const mod = 'cards.SummaryCard';

const useSummaryCardClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 20,
        color: theme.colors.neutral[2],
        ...theme.typography.content.caption.paragraph,
        transition: `
            padding ${theme.transitions.fast}ms,
            border-top ${theme.transitions.fast}ms,
            background ${theme.transitions.fast}ms,
            opacity ${theme.transitions.fast}ms
        `,
        '&.focused': {},
        '&.unfocused': {
            borderTop: '0px solid transparent',
            background: 'transparent',
            boxShadow: 'none',
            opacity: 0.5,
        },
        '&.completed': {
            opacity: 1,
        },
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 10,
        '&.completed': {
            color: theme.colors.green[4],
        },
    },
    labelIcon: {
        width: 20,
        height: 20,
        marginRight: 10,
    },
    badge: {
        marginBottom: 10,
    },
    title: {
        ...theme.typography.headings.h3,
        marginBottom: 5,
        whiteSpace: 'nowrap',
        '&.focused': {
            color: theme.colors.darkBlue[5],
        },
    },
    subtitle: {
        color: theme.colors.neutral[2],
        marginBottom: 10,
    },
    items: {
        display: 'flex',
        flexDirection: 'column',
        padding: '0 10px',
        ...theme.typography.content.label.medium,
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 0',
        '&.current': {
            color: theme.colors.darkBlue[5],
        },
        '&.completed': {
            color: theme.colors.green[4],
        },
        '&.remaining': {
            color: theme.colors.neutral[10],
        },
    },
    itemIcon: {
        width: 20,
        height: 20,
        marginRight: 10,
    },
});

/**
 * Small card to summarize the progress of some activity.
 *
 * The completed status is computed based on `props.items` and `props.progress`.
 * If the card is not focused, it does not display `props.items`, but still uses them to compute completion.
 *
 * @param props.focused if the card is highlighted
 * @param props.message text representing the current status, only used when the card is not focused and not completed
 * @param props.title card title
 * @param props.subtitle card subtitle
 * @param props.items list of ordered items to be tracked
 * @param props.progress number of items already finished
 */
export const SummaryCard = (props: {
    focused?: boolean;
    message?: string;
    title: string;
    subtitle?: string;
    items: string[];
    progress: number;
}) => {
    const classes = useSummaryCardClasses();
    const { t } = useTranslation(['inspection-creation']);
    const completed = props.progress >= props.items.length;
    const status = {
        focused: props.focused,
        unfocused: !props.focused,
        completed: completed,
        inProgress: !completed,
    };

    return (
        <HighlightCard className={clsx(classes.root, status)}>
            {completed || !props.focused ? (
                <span className={clsx(classes.label, status)}>
                    {completed && <CheckCircleOutlineSharp className={classes.labelIcon} />}
                    {completed ? t(`${mod}.completed`) : props.message}
                </span>
            ) : (
                <Badge className={classes.badge} type={'success'}>
                    {t(`${mod}.in-progress`)}
                </Badge>
            )}
            <span className={clsx(classes.title, status)}>{props.title}</span>
            {props.subtitle != undefined && <span className={classes.subtitle}>{props.subtitle}</span>}
            <div className={classes.items}>
                {props.focused &&
                    props.items.map((item, i) => {
                        const status = i < props.progress ? 'completed' : i > props.progress ? 'remaining' : 'current';
                        const Icon = i < props.progress ? Check : i > props.progress ? Circle : Clock;
                        return (
                            <div key={i} className={clsx(classes.item, status)}>
                                <Icon className={classes.itemIcon} />
                                {item}
                            </div>
                        );
                    })}
            </div>
        </HighlightCard>
    );
};
